import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Download, User, Activity, Eye,
  ChevronDown, ChevronUp, RefreshCw, FileText,
  AlertCircle, ChevronLeft, ChevronRight, Shield, Lock
} from 'lucide-react';
import { api, API_BASE_URL } from './services/apiClient';

const API_BASE = () => API_BASE_URL();
const PAGE_SIZE = 20;

const AuditModule = ({
  useAuth,
  useNotification,
  BaseButton,
  BaseInput,
  BaseCard
}) => {
  const { user } = useAuth();
  const { addNotification } = useNotification();

  // ── Pestaña activa ───────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('logs');

  const isSuperAdmin = user?.role === 'superadmin';

  // ── Estado pestaña Logs ──────────────────────────────────────────────────
  const [logs, setLogs]             = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(false);
  const [actionTypes, setActionTypes] = useState([]);
  const [expandedLog, setExpandedLog] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting]   = useState(false);
  const [filters, setFilters] = useState({
    search: '', dateFrom: '', dateTo: '', userId: '', action: ''
  });

  // ── Empresas (solo superadmin) ───────────────────────────────────────────
  const [companies, setCompanies]               = useState([]);
  const [selectedTenantIds, setSelectedTenantIds] = useState([]);
  const [companySearch, setCompanySearch]       = useState('');
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const companyDropdownRef = useRef(null);

  // ── Estado pestaña Seguridad ─────────────────────────────────────────────
  const [secLogs, setSecLogs]             = useState([]);
  const [secTotalCount, setSecTotalCount] = useState(0);
  const [secTotalPages, setSecTotalPages] = useState(1);
  const [secPage, setSecPage]             = useState(1);
  const [secLoading, setSecLoading]       = useState(false);
  const [secFilters, setSecFilters] = useState({
    dateFrom: '', dateTo: '', severity: ''
  });
  const [failedLoginsOnly, setFailedLoginsOnly] = useState(false);

  // ── Empresas seguridad (solo superadmin) ─────────────────────────────────
  const [secSelectedTenantIds, setSecSelectedTenantIds] = useState([]);
  const [secNoTenant, setSecNoTenant]                   = useState(false);
  const [secCompanySearch, setSecCompanySearch]         = useState('');
  const [secCompanyDropdownOpen, setSecCompanyDropdownOpen] = useState(false);
  const secCompanyDropdownRef = useRef(null);

  // ── Carga de datos — Logs ────────────────────────────────────────────────
  const loadLogs = async (currentPage, currentFilters, tenantIds = selectedTenantIds) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, pageSize: PAGE_SIZE });
      if (currentFilters.dateFrom) params.append('startDate', currentFilters.dateFrom);
      if (currentFilters.dateTo)   params.append('endDate',   currentFilters.dateTo + 'T23:59:59');
      if (currentFilters.userId)   params.append('userId',    currentFilters.userId);
      if (currentFilters.action)   params.append('action',    currentFilters.action);
      tenantIds.forEach(id => params.append('tenantIds', id));

      const data = await api.get(`/api/audit/logs?${params}`);
      setLogs(data.items || []);
      setTotalCount(data.totalCount || 0);
      setTotalPages(data.totalPages || 1);
    } catch {
      addNotification('Error al cargar los registros de auditoría', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadActionTypes = async () => {
    try {
      const data = await api.get('/api/audit/actions');
      setActionTypes(data || []);
    } catch { /* fallback silencioso */ }
  };

  const loadCompanies = async () => {
    try {
      const data = await api.get('/api/tenants');
      setCompanies(data || []);
    } catch { /* fallback silencioso */ }
  };

  // ── Carga de datos — Seguridad ───────────────────────────────────────────
  const loadSecurityLogs = async (currentPage, currentFilters, tenantIds = secSelectedTenantIds, noTenant = secNoTenant) => {
    setSecLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, pageSize: PAGE_SIZE });
      if (currentFilters.dateFrom) params.append('startDate', currentFilters.dateFrom);
      if (currentFilters.dateTo)   params.append('endDate',   currentFilters.dateTo + 'T23:59:59');
      if (currentFilters.severity) params.append('severity',  currentFilters.severity);
      if (noTenant) params.append('noTenant', 'true');
      tenantIds.forEach(id => params.append('tenantIds', id));

      const data = await api.get(`/api/audit/security?${params}`);
      setSecLogs(data.items || []);
      setSecTotalCount(data.totalCount || 0);
      setSecTotalPages(data.totalPages || 1);
    } catch {
      addNotification('Error al cargar los eventos de seguridad', 'error');
    } finally {
      setSecLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'superadmin') return;
    loadLogs(1, filters);
    loadActionTypes();
    if (isSuperAdmin) loadCompanies();
  }, []);

  useEffect(() => {
    if (!companyDropdownOpen) return;
    const handleClick = (e) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target))
        setCompanyDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [companyDropdownOpen]);

  useEffect(() => {
    if (!secCompanyDropdownOpen) return;
    const handleClick = (e) => {
      if (secCompanyDropdownRef.current && !secCompanyDropdownRef.current.contains(e.target))
        setSecCompanyDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [secCompanyDropdownOpen]);

  useEffect(() => {
    if (activeTab === 'security' && (user?.role === 'admin' || user?.role === 'superadmin') && secLogs.length === 0) {
      loadSecurityLogs(1, secFilters);
    }
  }, [activeTab]);

  // ── Acciones — Logs ──────────────────────────────────────────────────────
  const applyFilters = () => { setPage(1); loadLogs(1, filters); };
  const clearFilters = () => {
    const empty = { search: '', dateFrom: '', dateTo: '', userId: '', action: '' };
    setFilters(empty); setPage(1); loadLogs(1, empty);
  };
  const handlePageChange = (newPage) => { setPage(newPage); loadLogs(newPage, filters); };

  const toggleTenant = (id) => {
    const next = selectedTenantIds.includes(id)
      ? selectedTenantIds.filter(t => t !== id)
      : [...selectedTenantIds, id];
    setSelectedTenantIds(next);
    setPage(1);
    loadLogs(1, filters, next);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({ format: 'csv' });
      if (filters.dateFrom) params.append('startDate', filters.dateFrom);
      if (filters.dateTo)   params.append('endDate',   filters.dateTo + 'T23:59:59');

      const response = await fetch(`${API_BASE()}/api/audit/export?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `auditoria_${new Date().toISOString().split('T')[0]}.csv`;
      a.click(); URL.revokeObjectURL(url);
      addNotification('Archivo exportado exitosamente', 'success');
    } catch {
      addNotification('Error al exportar los registros', 'error');
    } finally {
      setExporting(false);
    }
  };

  // ── Acciones — Seguridad ─────────────────────────────────────────────────
  const applySecFilters = () => { setSecPage(1); loadSecurityLogs(1, secFilters, secSelectedTenantIds, secNoTenant); };
  const clearSecFilters = () => {
    const empty = { dateFrom: '', dateTo: '', severity: '' };
    setSecFilters(empty); setSecPage(1);
    setSecSelectedTenantIds([]); setSecNoTenant(false);
    loadSecurityLogs(1, empty, [], false);
  };
  const handleSecPageChange = (newPage) => { setSecPage(newPage); loadSecurityLogs(newPage, secFilters, secSelectedTenantIds, secNoTenant); };

  const toggleSecTenant = (id) => {
    const next = secSelectedTenantIds.includes(id)
      ? secSelectedTenantIds.filter(t => t !== id)
      : [...secSelectedTenantIds, id];
    setSecSelectedTenantIds(next);
    setSecPage(1);
    loadSecurityLogs(1, secFilters, next, secNoTenant);
  };

  const toggleSecNoTenant = () => {
    const next = !secNoTenant;
    setSecNoTenant(next);
    setSecPage(1);
    loadSecurityLogs(1, secFilters, secSelectedTenantIds, next);
  };

  const secDropdownLabel = () => {
    const n = secSelectedTenantIds.length;
    if (secNoTenant && n > 0) return `Sin empresa + Empresas (${n})`;
    if (secNoTenant)          return 'Sin empresa';
    if (n > 0)                return `Empresas (${n})`;
    return 'Todas las empresas';
  };

  // ── Búsqueda local en Logs ───────────────────────────────────────────────
  const displayedLogs = filters.search.trim()
    ? logs.filter(log => {
        const q = filters.search.toLowerCase();
        return (
          log.userId?.toLowerCase().includes(q) ||
          log.action?.toLowerCase().includes(q) ||
          log.entityType?.toLowerCase().includes(q) ||
          log.entityId?.toLowerCase().includes(q) ||
          log.details?.toLowerCase().includes(q)
        );
      })
    : logs;

  // ── Filtro logins fallidos (client-side) ─────────────────────────────────
  const displayedSecLogs = failedLoginsOnly
    ? secLogs.filter(log => {
        const action = log.action?.toUpperCase() || '';
        const result = log.result?.toLowerCase() || '';
        const category = log.category?.toUpperCase() || '';
        const isLogin = action.includes('LOGIN') || category.includes('AUTH');
        const isFailed = result.includes('fail') || result.includes('error') || result === 'failure';
        return isLogin && isFailed;
      })
    : secLogs;

  // ── Helpers de presentación ──────────────────────────────────────────────
  const getStatusBadge = (result) => {
    const r = result?.toLowerCase();
    if (r === 'success')                  return 'bg-green-100 text-green-800';
    if (r === 'warning')                  return 'bg-yellow-100 text-yellow-800';
    if (r === 'error' || r === 'failure') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getSeverityBadge = (severity) => {
    const s = severity?.toLowerCase();
    if (s === 'critical' || s === 'high') return 'bg-red-100 text-red-800';
    if (s === 'medium' || s === 'warning') return 'bg-yellow-100 text-yellow-800';
    if (s === 'low' || s === 'info')      return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getActionIcon = (action) => {
    const a = action?.toUpperCase() || '';
    if (a.includes('CREATE') || a.includes('UPLOAD'))
      return <FileText className="text-green-600" size={16} />;
    if (a.includes('UPDATE') || a.includes('CLONE'))
      return <Activity className="text-blue-600" size={16} />;
    if (a.includes('DELETE'))
      return <AlertCircle className="text-red-600" size={16} />;
    if (a.includes('LOGIN') || a.includes('LOGOUT'))
      return <User className="text-indigo-600" size={16} />;
    if (a.includes('DOWNLOAD') || a.includes('EXPORT') || a.includes('REPORT'))
      return <Download className="text-primary" size={16} />;
    return <Activity className="text-gray-600" size={16} />;
  };

  // ── Guard — solo admins ──────────────────────────────────────────────────
  if (user?.role !== 'admin' && user?.role !== 'superadmin') {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Shield className="mx-auto mb-4 text-red-400" size={48} />
          <h3 className="text-lg font-medium text-gray-900">Acceso restringido</h3>
          <p className="text-gray-500 mt-1">Solo los administradores pueden ver la auditoría.</p>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* Pestañas */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('logs')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'logs'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Activity size={16} />
            Logs de Acciones
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'security'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Lock size={16} />
            Seguridad
          </button>
        </nav>
      </div>

      {/* ══ PESTAÑA LOGS ══════════════════════════════════════════════════════ */}
      {activeTab === 'logs' && (
        <>
          {/* Selector de empresas — solo superadmin */}
          {isSuperAdmin && companies.length > 0 && (() => {
            const filtered = companies
              .filter(c => c.name.toLowerCase().includes(companySearch.toLowerCase()))
              .sort((a, b) => a.name.localeCompare(b.name));
            return (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative" ref={companyDropdownRef}>
                  <button
                    onClick={() => setCompanyDropdownOpen(o => !o)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:border-primary hover:text-primary transition-colors shadow-sm"
                  >
                    <Shield size={15} />
                    {selectedTenantIds.length > 0 ? `Empresas (${selectedTenantIds.length})` : 'Todas las empresas'}
                    <ChevronDown size={14} className={`transition-transform ${companyDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {companyDropdownOpen && (
                    <div
                      className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg"
                      style={{ width: '280px' }}
                      onMouseDown={e => e.stopPropagation()}
                    >
                      {/* Búsqueda */}
                      <div className="p-2 border-b border-gray-100">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                          <Search size={13} className="text-gray-400 flex-shrink-0" />
                          <input
                            autoFocus
                            type="text"
                            placeholder="Buscar empresa..."
                            value={companySearch}
                            onChange={e => setCompanySearch(e.target.value)}
                            className="bg-transparent text-sm outline-none w-full text-gray-700 placeholder-gray-400"
                          />
                        </div>
                      </div>

                      {/* Lista con checkboxes */}
                      <div className="overflow-y-auto" style={{ maxHeight: '260px' }}>
                        {filtered.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-6">Sin resultados</p>
                        ) : filtered.map(c => {
                          const checked = selectedTenantIds.includes(c.id);
                          return (
                            <label
                              key={c.id}
                              className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleTenant(c.id)}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm text-gray-700 truncate">{c.name}</span>
                            </label>
                          );
                        })}
                      </div>

                      {/* Footer */}
                      {selectedTenantIds.length > 0 && (
                        <div className="p-2 border-t border-gray-100">
                          <button
                            onClick={() => { setSelectedTenantIds([]); setPage(1); loadLogs(1, filters, []); setCompanyDropdownOpen(false); }}
                            className="w-full text-xs text-red-500 hover:text-red-700 py-1 font-medium"
                          >
                            Limpiar selección
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Tags de empresas seleccionadas */}
                {selectedTenantIds.map(id => {
                  const c = companies.find(x => x.id === id);
                  return c ? (
                    <span key={id} className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                      {c.name}
                      <button onClick={() => toggleTenant(id)} className="hover:text-red-500 leading-none">&times;</button>
                    </span>
                  ) : null;
                })}
              </div>
            );
          })()}

          {/* Tarjetas resumen */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <BaseCard className="text-center">
              <Activity className="mx-auto mb-3 text-blue-600" size={32} />
              <h3 className="font-semibold text-gray-900">Total Registros</h3>
              <p className="text-2xl font-bold text-gray-800 mt-2">{totalCount}</p>
            </BaseCard>
            <BaseCard className="text-center">
              <User className="mx-auto mb-3 text-indigo-600" size={32} />
              <h3 className="font-semibold text-gray-900">Tipos de Acción</h3>
              <p className="text-2xl font-bold text-gray-800 mt-2">{actionTypes.length}</p>
            </BaseCard>
            <BaseCard className="text-center">
              <Eye className="mx-auto mb-3 text-green-600" size={32} />
              <h3 className="font-semibold text-gray-900">Mostrando</h3>
              <p className="text-2xl font-bold text-gray-800 mt-2">{displayedLogs.length}</p>
            </BaseCard>
          </div>

          {/* Barra de filtros */}
          <BaseCard>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-3">
                <BaseInput
                  placeholder="Buscar en registros..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  icon={Search}
                  className="w-64"
                />
                <BaseButton
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  icon={showFilters ? ChevronUp : ChevronDown}
                >
                  Filtros
                </BaseButton>
              </div>
              <div className="flex items-center gap-2">
                <BaseButton variant="outline" onClick={() => loadLogs(page, filters)} icon={RefreshCw} size="sm">
                  Actualizar
                </BaseButton>
                <BaseButton variant="secondary" onClick={handleExport} icon={Download} size="sm" disabled={exporting}>
                  {exporting ? 'Exportando...' : 'Exportar CSV'}
                </BaseButton>
              </div>
            </div>

            {showFilters && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <BaseInput label="Fecha Desde" type="date" value={filters.dateFrom}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))} />
                  <BaseInput label="Fecha Hasta" type="date" value={filters.dateTo}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))} />
                  <BaseInput label="Usuario" placeholder="Filtrar por usuario" value={filters.userId}
                    onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))} />
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">Acción</label>
                    <select
                      value={filters.action}
                      onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Todas las acciones</option>
                      {actionTypes.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <BaseButton variant="outline" onClick={clearFilters} size="sm">Limpiar</BaseButton>
                  <BaseButton onClick={applyFilters} size="sm">Aplicar</BaseButton>
                </div>
              </div>
            )}
          </BaseCard>

          {/* Tabla de logs */}
          <BaseCard title="Registros de Auditoría">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3" />
                <span className="text-gray-600">Cargando registros...</span>
              </div>
            ) : displayedLogs.length === 0 ? (
              <div className="text-center py-12">
                <Search className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600">No se encontraron registros con los filtros aplicados</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Fecha y Hora</th>
                        {isSuperAdmin && <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Empresa</th>}
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Usuario</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Acción</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Entidad</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">ID</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Estado</th>
                        <th className="py-3 px-4" />
                      </tr>
                    </thead>
                    <tbody>
                      {displayedLogs.map(log => (
                        <React.Fragment key={log.id}>
                          <tr className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                              {new Date(log.timestamp).toLocaleString('es-ES')}
                            </td>
                            {isSuperAdmin && (
                              <td className="py-3 px-4 text-sm text-gray-700">
                                {companies.find(c => c.id === log.tenantId)?.name || `Tenant ${log.tenantId}`}
                              </td>
                            )}
                            <td className="py-3 px-4 text-sm font-medium text-gray-900">{log.userId}</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {getActionIcon(log.action)}
                                <span className="text-sm text-gray-900">{log.action}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-900">{log.entityType || '—'}</td>
                            <td className="py-3 px-4 text-sm text-gray-500">{log.entityId || '—'}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(log.result)}`}>
                                {log.result || '—'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              {log.details && (
                                <BaseButton
                                  variant="outline" size="sm"
                                  onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                                  icon={expandedLog === log.id ? ChevronUp : ChevronDown}
                                >
                                  Detalles
                                </BaseButton>
                              )}
                            </td>
                          </tr>
                          {expandedLog === log.id && (
                            <tr className="bg-gray-50">
                              <td colSpan="7" className="py-4 px-4">
                                <div className="bg-white p-4 rounded-lg border border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-gray-500 font-medium mb-1">Descripción</p>
                                    <p className="text-gray-900">{log.details}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500 font-medium mb-1">IP</p>
                                    <p className="text-gray-900">{log.ipAddress || '—'}</p>
                                  </div>
                                  {log.durationMs > 0 && (
                                    <div>
                                      <p className="text-gray-500 font-medium mb-1">Duración</p>
                                      <p className="text-gray-900">{log.durationMs} ms</p>
                                    </div>
                                  )}
                                  {log.userAgent && (
                                    <div className="md:col-span-2">
                                      <p className="text-gray-500 font-medium mb-1">User Agent</p>
                                      <p className="text-gray-900 break-all text-xs">{log.userAgent}</p>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                    <p className="text-sm text-gray-600">
                      Página {page} de {totalPages} — {totalCount} registros totales
                    </p>
                    <div className="flex items-center gap-2">
                      <BaseButton variant="outline" size="sm" onClick={() => handlePageChange(page - 1)}
                        disabled={page <= 1} icon={ChevronLeft}>Anterior</BaseButton>
                      <BaseButton variant="outline" size="sm" onClick={() => handlePageChange(page + 1)}
                        disabled={page >= totalPages} icon={ChevronRight}>Siguiente</BaseButton>
                    </div>
                  </div>
                )}
              </>
            )}
          </BaseCard>
        </>
      )}

      {/* ══ PESTAÑA SEGURIDAD ════════════════════════════════════════════════ */}
      {activeTab === 'security' && (
        <>
          {/* Selector de empresas — solo superadmin */}
          {isSuperAdmin && companies.length > 0 && (() => {
            const filtered = companies
              .filter(c => c.name.toLowerCase().includes(secCompanySearch.toLowerCase()))
              .sort((a, b) => a.name.localeCompare(b.name));
            const anySelected = secNoTenant || secSelectedTenantIds.length > 0;
            return (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative" ref={secCompanyDropdownRef}>
                  <button
                    onClick={() => setSecCompanyDropdownOpen(o => !o)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:border-primary hover:text-primary transition-colors shadow-sm"
                  >
                    <Shield size={15} />
                    {secDropdownLabel()}
                    <ChevronDown size={14} className={`transition-transform ${secCompanyDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {secCompanyDropdownOpen && (
                    <div
                      className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg"
                      style={{ width: '280px' }}
                      onMouseDown={e => e.stopPropagation()}
                    >
                      {/* Búsqueda */}
                      <div className="p-2 border-b border-gray-100">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                          <Search size={13} className="text-gray-400 flex-shrink-0" />
                          <input
                            autoFocus
                            type="text"
                            placeholder="Buscar empresa..."
                            value={secCompanySearch}
                            onChange={e => setSecCompanySearch(e.target.value)}
                            className="bg-transparent text-sm outline-none w-full text-gray-700 placeholder-gray-400"
                          />
                        </div>
                      </div>

                      {/* Lista con checkboxes */}
                      <div className="overflow-y-auto" style={{ maxHeight: '260px' }}>
                        {/* Opción especial: Sin empresa */}
                        {!'sin empresa'.includes(secCompanySearch.toLowerCase()) ? null : (
                          <label className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                            <input
                              type="checkbox"
                              checked={secNoTenant}
                              onChange={toggleSecNoTenant}
                              className="rounded border-gray-300 text-primary focus:ring-primary"
                            />
                            <span className="text-sm text-gray-500 italic">Sin empresa</span>
                          </label>
                        )}

                        {filtered.length === 0 && secCompanySearch && !secCompanySearch.toLowerCase().startsWith('sin') ? (
                          <p className="text-xs text-gray-400 text-center py-6">Sin resultados</p>
                        ) : filtered.map(c => {
                          const checked = secSelectedTenantIds.includes(c.id);
                          return (
                            <label
                              key={c.id}
                              className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSecTenant(c.id)}
                                className="rounded border-gray-300 text-primary focus:ring-primary"
                              />
                              <span className="text-sm text-gray-700 truncate">{c.name}</span>
                            </label>
                          );
                        })}
                      </div>

                      {/* Footer */}
                      {anySelected && (
                        <div className="p-2 border-t border-gray-100">
                          <button
                            onClick={() => {
                              setSecSelectedTenantIds([]); setSecNoTenant(false);
                              setSecPage(1); loadSecurityLogs(1, secFilters, [], false);
                              setSecCompanyDropdownOpen(false);
                            }}
                            className="w-full text-xs text-red-500 hover:text-red-700 py-1 font-medium"
                          >
                            Limpiar selección
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Tags */}
                {secNoTenant && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                    Sin empresa
                    <button onClick={toggleSecNoTenant} className="hover:text-red-500 leading-none">&times;</button>
                  </span>
                )}
                {secSelectedTenantIds.map(id => {
                  const c = companies.find(x => x.id === id);
                  return c ? (
                    <span key={id} className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                      {c.name}
                      <button onClick={() => toggleSecTenant(id)} className="hover:text-red-500 leading-none">&times;</button>
                    </span>
                  ) : null;
                })}
              </div>
            );
          })()}

          {/* Tarjetas resumen */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BaseCard className="text-center">
              <Shield className="mx-auto mb-3 text-indigo-600" size={32} />
              <h3 className="font-semibold text-gray-900">Eventos de Seguridad</h3>
              <p className="text-2xl font-bold text-gray-800 mt-2">{secTotalCount}</p>
            </BaseCard>
            <BaseCard className="text-center">
              <AlertCircle className="mx-auto mb-3 text-red-500" size={32} />
              <h3 className="font-semibold text-gray-900">Eventos Críticos/Altos</h3>
              <p className="text-2xl font-bold text-gray-800 mt-2">
                {secLogs.filter(l => ['critical','high'].includes(l.severity?.toLowerCase())).length}
              </p>
            </BaseCard>
          </div>

          {/* Filtros seguridad */}
          <BaseCard>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <BaseInput label="" placeholder="Fecha desde" type="date" value={secFilters.dateFrom}
                  onChange={(e) => setSecFilters(prev => ({ ...prev, dateFrom: e.target.value }))} />
                <BaseInput label="" placeholder="Fecha hasta" type="date" value={secFilters.dateTo}
                  onChange={(e) => setSecFilters(prev => ({ ...prev, dateTo: e.target.value }))} />
                <div className="space-y-1">
                  <select
                    value={secFilters.severity}
                    onChange={(e) => setSecFilters(prev => ({ ...prev, severity: e.target.value }))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">Todas las severidades</option>
                    <option value="critical">Crítico</option>
                    <option value="high">Alto</option>
                    <option value="medium">Medio</option>
                    <option value="low">Bajo</option>
                    <option value="info">Info</option>
                  </select>
                </div>
                <BaseButton onClick={applySecFilters} size="sm">Aplicar</BaseButton>
                <BaseButton variant="outline" onClick={clearSecFilters} size="sm">Limpiar</BaseButton>
                <button
                  onClick={() => setFailedLoginsOnly(prev => !prev)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    failedLoginsOnly
                      ? 'bg-red-100 border-red-400 text-red-700'
                      : 'bg-white border-gray-300 text-gray-500 hover:border-red-400 hover:text-red-600'
                  }`}
                >
                  <Lock size={12} />
                  Solo logins fallidos
                </button>
              </div>
              <BaseButton variant="outline" onClick={() => loadSecurityLogs(secPage, secFilters)} icon={RefreshCw} size="sm">
                Actualizar
              </BaseButton>
            </div>
          </BaseCard>

          {/* Tabla seguridad */}
          <BaseCard title="Eventos de Seguridad">
            {secLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-3" />
                <span className="text-gray-600">Cargando eventos de seguridad...</span>
              </div>
            ) : displayedSecLogs.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600">
                  {failedLoginsOnly ? 'No se encontraron intentos de login fallidos' : 'No se encontraron eventos de seguridad'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Fecha y Hora</th>
                        {isSuperAdmin && <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Empresa</th>}
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Usuario</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Severidad</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Categoría</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Acción</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Mensaje</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">IP</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900 text-sm">Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedSecLogs.map(log => (
                        <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-900 whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleString('es-ES')}
                          </td>
                          {isSuperAdmin && (
                            <td className="py-3 px-4 text-sm text-gray-700">
                              {log.tenantId === 0
                                ? <span className="text-gray-400 italic">Sin empresa</span>
                                : companies.find(c => c.id === log.tenantId)?.name || `Tenant ${log.tenantId}`}
                            </td>
                          )}
                          <td className="py-3 px-4 text-sm font-medium text-gray-900">{log.userId || '—'}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 text-xs rounded-full font-semibold ${getSeverityBadge(log.severity)}`}>
                              {log.severity || '—'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-700">{log.category || '—'}</td>
                          <td className="py-3 px-4 text-sm text-gray-700">{log.action || '—'}</td>
                          <td className="py-3 px-4 text-sm text-gray-900 max-w-xs truncate" title={log.message}>
                            {log.message || '—'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-500">{log.ipAddress || '—'}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(log.result)}`}>
                              {log.result || '—'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {secTotalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                    <p className="text-sm text-gray-600">
                      Página {secPage} de {secTotalPages} — {secTotalCount} eventos totales
                    </p>
                    <div className="flex items-center gap-2">
                      <BaseButton variant="outline" size="sm" onClick={() => handleSecPageChange(secPage - 1)}
                        disabled={secPage <= 1} icon={ChevronLeft}>Anterior</BaseButton>
                      <BaseButton variant="outline" size="sm" onClick={() => handleSecPageChange(secPage + 1)}
                        disabled={secPage >= secTotalPages} icon={ChevronRight}>Siguiente</BaseButton>
                    </div>
                  </div>
                )}
              </>
            )}
          </BaseCard>
        </>
      )}
    </div>
  );
};

export default AuditModule;
